import React, { useState, useEffect, useCallback, useRef } from "react";
import { InfoIconWithMessage } from "../../../../../shared/infoIcon/InfoIcon";
import { ValidatorForm } from "react-material-ui-form-validator";
import ValidationRules from "../../../../../common/ValidationRules";
import ReservationList from "./ReservationList";
import { withTranslation } from "react-i18next";
import styles from "../../../../businessSetting/merchantId/MerchantId.module.scss";
import SubNav from "../../../../../shared/subnav/SubNav";
import HasPermissionTo from "../../../../../common/HasPermissionTo";
import BackToButton from "../../../../../shared/backToButton/BackToButton";
import ReservationPriceClient from "../../../../../shared/clients/ReservationPriceClient";
import { DEALER, MACHE, REGEX_LIST } from "../../../../../common/Constants";
import commonStyles from "../../../../../styles/common/CommonStyles.module.scss";
import { PricesApplied } from "../../../../../shared/feedback/PricesApplied";
import UpperCaseText from "../../../../../utils/UpperCaseText";
import { IS_AUS_MARKET } from "../../../../../utils/EmpUtil";

function ModifyReservationPrice(props) {
  const [reservations, setReservations] = useState([]);
  const [isEditable, setIsEditable] = useState(
    props.fromQuickstart ? props.isNsc : false
  );
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [buttonEnabled, setButtonEnabled] = useState(true);
  const [editButtonDisable, setEditButtonDisable] = useState({});
  const [allChecked, setAllChecked] = useState(false);
  const [finalReservations, setFinalReservations] = useState([]);
  const [vehicleLineMap, setVehicleLineMap] = useState({});
  const [originalDeposit, setOriginalDeposit] = useState([]);

  const formRef = useRef();
  const userRef = useRef(props.user);
  const isNscRef = useRef(props.isNsc);
  const fromQuickstartRef = useRef(props.fromQuickstart);
  const dealerGroupRef = useRef(props.dealerGroup);

  useEffect(() => {
    userRef.current = props.user;
    isNscRef.current = props.isNsc;
    fromQuickstartRef.current = props.fromQuickstart;
    dealerGroupRef.current = props.dealerGroup;
  }, [props.user, props.isNsc, props.fromQuickstart, props.dealerGroup]);

  const handleBevVehicles = useCallback((result) => {
    const macheIndex = result
      .map((vehicle) => UpperCaseText(vehicle.model))
      .indexOf(MACHE);
    if (macheIndex !== -1) {
      if (fromQuickstartRef.current) {
        const bevEnabledIndex = dealerGroupRef.current
          ?.map((dealer) => dealer.isBevDealer)
          .indexOf(true);
        if (bevEnabledIndex === -1) {
          result.splice(macheIndex, 1);
        }
      } else {
        const currentDealer = userRef.current.getCurrentDealer();
        if (!currentDealer.isBevDealer) {
          result.splice(macheIndex, 1);
        }
      }
    }
    return result;
  }, []);

  const getVehicleLineMap = useCallback((reservations) => {
    const vehicleLineMapTemp = {};
    reservations.forEach((reservation) => {
      vehicleLineMapTemp[reservation.vehicleLine] = reservation.catalogId;
    });
    setVehicleLineMap(vehicleLineMapTemp);
  }, []);

  const removeDuplicates = useCallback((reservations) => {
    const seenModels = new Map();
    const filteredReservations = [];
    reservations.forEach((reservation) => {
      if (seenModels.has(reservation.model)) {
        const functionToFindModel = (element) =>
          element.model === reservation.model;
        const indexOfReservation =
          filteredReservations.findIndex(functionToFindModel);

        filteredReservations[indexOfReservation].newVehicleLine =
          reservation.newVehicleLine && reservation.newVehicleLine;

        if (!isNscRef.current) {
          filteredReservations[indexOfReservation].amount = reservation.amount;
        } else {
          filteredReservations[indexOfReservation].amount =
            !reservation.newVehicleLine ? reservation.amount : null;
        }
        if (
          Array.isArray(filteredReservations[indexOfReservation].vehicleLine)
        ) {
          filteredReservations[indexOfReservation].vehicleLine.push(
            reservation.vehicleLine
          );
        } else {
          filteredReservations[indexOfReservation].vehicleLine = [
            filteredReservations[indexOfReservation].vehicleLine,
            reservation.vehicleLine,
          ];
        }
      } else {
        seenModels.set(reservation.model, 0);
        filteredReservations.push(reservation);
      }
    });

    return filteredReservations;
  }, []);

  const minNumber = useCallback(() => {
    const localMarket = userRef.current.market;
    if (
      UpperCaseText(localMarket) === "ZAF" ||
      UpperCaseText(localMarket) === "THA"
    ) {
      return 0;
    }
    return 1;
  }, []);

  useEffect(() => {
    if (props.fromQuickstart && props.showActionFooter) {
      props.showActionFooter(true);
    }

    ValidationRules();

    if (props.hideOrShow) {
      props.hideOrShow(true);
    }

    ReservationPriceClient.retrieveReservationPrices(props.user)
      .then((result) => {
        getVehicleLineMap(result);

        if (
          IS_AUS_MARKET(props.user.market) &&
          UpperCaseText(props.user.userType) === DEALER
        ) {
          result = handleBevVehicles(result);
        }

        const listOfVehicles = [];
        for (let idx = 0; idx < result.length; ++idx) {
          if (
            !isNaN(result[idx].amount) &&
            result[idx].amount !== null &&
            result[idx].amount !== ""
          ) {
            result[idx].amount = parseFloat(result[idx].amount).toFixed(2);
            result[idx].checked = false;
          }
          listOfVehicles.push(result[idx]);
        }

        const processedReservations = removeDuplicates(listOfVehicles);
        setReservations(processedReservations);
        setOriginalDeposit(result);

        if (props.fromQuickstart) {
          const minNum = minNumber();
          const checkedReservations = processedReservations.map(reservation => {
            if (reservation.amount && parseFloat(reservation.amount) >= minNum) {
              return { ...reservation, checked: true };
            }
            return reservation;
          });
          setReservations(checkedReservations);
          setAllChecked(true);
          setButtonEnabled(true);
          setIsEditable(false);
        }
      })
      .catch((error) => {
        console.error("Error loading reservation prices:", error);
      })
      .finally(() => {
        if (props.hideOrShow) {
          props.hideOrShow(false);
        }
        setIsDataLoaded(true);
      });
  }, []);

  const checkIfAllChecked = useCallback((filteredReservations, allReservations) => {
    return filteredReservations.length === allReservations.length;
  }, []);

  const clickCheckBox = useCallback((event) => {
    const index = parseInt(event.target.value);
    setReservations(prevReservations => {
      const newReservations = [...prevReservations];
      newReservations[index] = {
        ...newReservations[index],
        checked: !newReservations[index].checked
      };

      const filteredReservations = newReservations.filter(
        (reservation) => reservation.checked === true
      );

      setAllChecked(checkIfAllChecked(filteredReservations, newReservations));
      setButtonEnabled(true);

      return newReservations;
    });
  }, [checkIfAllChecked]);

  const EditReservationsHandler = useCallback(() => {
    setIsEditable(prev => !prev);
  }, []);

  const setEditModeToFalse = useCallback(() => {
    setIsEditable(false);
  }, []);

  const uncheckEverything = useCallback(() => {
    if (!fromQuickstartRef.current) {
      setReservations(prevReservations => {
        return prevReservations.map(reservation => ({
          ...reservation,
          checked: false
        }));
      });
    }
  }, []);

  const convertReservations = useCallback((reservationList) => {
    const newReservations = [];
    reservationList.forEach((reservation) => {
      if (reservation.checked) {
        if (Array.isArray(reservation.vehicleLine)) {
          reservation.vehicleLine.forEach((vehicleLine) => {
            const newReservation = { ...reservation };
            newReservation.vehicleLine = vehicleLine;
            newReservation.catalogId = vehicleLineMap[vehicleLine];
            newReservations.push(newReservation);
          });
        } else {
          newReservations.push(reservation);
        }
      }
    });
    return newReservations;
  }, [vehicleLineMap]);

  const uploadPrice = useCallback(() => {
    const filteredReservations = reservations.filter(
      (reservation) => reservation.checked === true
    );
    const tempRes = JSON.parse(JSON.stringify(filteredReservations));
    setFinalReservations(tempRes);

    for (let idx = 0; idx < tempRes.length; ++idx) {
      if (
        !isNaN(tempRes[idx].amount) &&
        tempRes[idx].amount !== null &&
        tempRes[idx].amount !== ""
      ) {
        tempRes[idx].amount = parseFloat(tempRes[idx].amount).toFixed(2);
      }
    }

    setOriginalDeposit(reservations);
    setIsEditable(false);
    setButtonEnabled(true);
    setEditButtonDisable({});
    setAllChecked(false);

    if (props.onSubmit) {
      props.onSubmit({
        reservations: convertReservations(reservations),
      });
    }

    uncheckEverything();
  }, [reservations, props.onSubmit, convertReservations, uncheckEverything]);

  const updateReservation = useCallback((event) => {
    const { value, name } = event.target;

    setEditButtonDisable(prev => ({
      ...prev,
      [name]: !value.match(REGEX_LIST.number) || !value
    }));

    setReservations(prevReservations => {
      return prevReservations.map((reservation) => {
        if (name === reservation.model) {
          return { ...reservation, amount: value };
        }
        return reservation;
      });
    });

    setButtonEnabled(true);
  }, []);

  const containsDisabled = useCallback(() => {
    return Object.values(editButtonDisable).includes(true);
  }, [editButtonDisable]);

  const containsBlank = useCallback(() => {
    return reservations.some(
      reservation => reservation.checked === true && reservation.amount === null
    );
  }, [reservations]);

  const dealerCanEditReservationPrice = useCallback(() => {
    if (!props.isNsc && props.user?.dealerGroup && props.isMainViewingSDLR) {
      const currentDealer = props.user.dealerGroup.filter(
        (dealer) => dealer.commonId === props.user.commonId
      )[0];
      if (currentDealer) {
        const isMainViewingSDLR = props.isMainViewingSDLR(currentDealer);
        return !isMainViewingSDLR;
      }
    }
    return true;
  }, [props.isNsc, props.user, props.isMainViewingSDLR]);

  const getCount = useCallback((reservationList) => {
    const count = reservationList.filter(reservation => reservation.checked).length;
    return count > 0 ? count : "";
  }, []);

  const getWording = useCallback((reservationList) => {
    const count = reservationList.filter(reservation => reservation.checked).length;
    return count === 1
      ? props.t("ActionFooter.orderDeposit")
      : props.t("ActionFooter.orderDeposits");
  }, [props.t]);

  const fillAllCheckBox = useCallback(() => {
    const checkedOrNot = !allChecked;
    const minNum = minNumber();

    setReservations(prevReservations => {
      const newReservations = prevReservations.map(reservation => {
        if (reservation.amount && parseFloat(reservation.amount) >= minNum) {
          return { ...reservation, checked: checkedOrNot };
        }
        if (checkedOrNot === false) {
          return { ...reservation, checked: false };
        }
        return reservation;
      });
      return newReservations;
    });

    setAllChecked(checkedOrNot);
    setButtonEnabled(checkedOrNot);
    setIsEditable(false);
  }, [allChecked, minNumber]);

  const applyBtnHandler = useCallback((enable) => {
    setButtonEnabled(enable);
  }, []);

  const disableSaveButton = useCallback(() => {
    return (
      !buttonEnabled ||
      containsDisabled() ||
      containsBlank() ||
      !(getCount(reservations) > 0)
    );
  }, [buttonEnabled, containsDisabled, containsBlank, getCount, reservations]);

  const getSubNav = useCallback(() => {
    return (
      <>
        <BackToButton
          backToUrl="/pricing"
          display={props.t("PricingWrapper.backToPricing")}
        />
        <SubNav
          user={props.user}
          onDealerChanged={props.onDealerChanged}
          t={props.t}
          title={props.t("ModifyReservationPrice.header")}
          renderSubHeader={false}
          subHeader={null}
          userDetails={props.userDetails}
          isNsc={props.isNsc}
          localizedInfo={props.localizedInfo}
        />
      </>
    );
  }, [props]);

  if (!isDataLoaded) {
    return <div>Loading...</div>;
  }

  const subheader = props.isNsc
    ? props.t("ModifyReservationPrice.subheaderNsc")
    : !props.fromQuickstart
      ? props.t("ModifyReservationPrice.subheaderPricing")
      : props.t("ModifyReservationPrice.subheader");

  return (
    <div className="pageWrapper">
      {!props.fromQuickstart && getSubNav()}
      <div className="pagePaddingLeft pagePaddingTop">
        {props.fromQuickstart && (
          <h1 id="depositPrice" className="pageTitle">
            {props.t("ModifyReservationPrice.header")}
          </h1>
        )}
        <InfoIconWithMessage message={subheader} />
      </div>
      <ValidatorForm
        id="modifyReservationForm"
        ref={props.formRef || formRef}
        onSubmit={uploadPrice}
      >
        <ReservationList
          {...props}
          originalDeposit={originalDeposit}
          reservations={reservations}
          updateReservation={updateReservation}
          editMode={isEditable}
          editReservations={EditReservationsHandler}
          applyBtn={applyBtnHandler}
          removeEditMode={setEditModeToFalse}
          dealerCanEditReservationPrice={dealerCanEditReservationPrice}
          clickCheckBox={clickCheckBox}
          editButtonDisable={containsDisabled()}
          checkAllCheckBox={fillAllCheckBox}
          allChecked={allChecked}
        />
        <HasPermissionTo
          perform={["depositPrice:write"]}
          permissions={props.user?.permissions?.rolePermissions}
          condition={dealerCanEditReservationPrice()}
          render={() =>
            !props.fromQuickstart &&
            getCount(reservations) > 0 ? (
              <div
                className={
                  disableSaveButton()
                    ? styles.disabledButtonDiv
                    : styles.buttonDiv
                }
              >
                <button
                  disabled={disableSaveButton()}
                  id="saveBtn"
                  className={commonStyles.greenBtn}
                >
                  {`${props.t("ActionFooter.apply")} ${getCount(
                    reservations
                  )} ${getWording(reservations)}`}
                </button>
              </div>
            ) : (
              <PricesApplied
                success={props.pricesApplied}
                text={`${getCount(
                  finalReservations
                )} ${getWording(
                  finalReservations
                )} ${props.t("ModifyReservationPrice.published")}`}
              />
            )
          }
        />
        <div className={styles.errorMsg}>{props.errorMessage}</div>
      </ValidatorForm>
    </div>
  );
}

export default withTranslation("emp")(ModifyReservationPrice);
